import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert
} from '@mui/material';
import {
  Add,
  CheckCircle,
  Edit
} from '@mui/icons-material';
import api from '../lib/api';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

const AdaptationTracker: React.FC = () => {
  const { id: agreementId } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [openDialog, setOpenDialog] = useState(false);
  const [formData, setFormData] = useState({
    componentId: '',
    adaptationType: 'MODIFICATION',
    originalPlan: '',
    adaptedPlan: '',
    reason: ''
  });

  const { data: adaptations, isLoading } = useQuery({
    queryKey: ['adaptations', agreementId],
    queryFn: async () => {
      const res = await api.get(`/adaptations?agreementId=${agreementId}`);
      return res.data.data;
    },
    enabled: !!agreementId
  });

  const { data: components } = useQuery({
    queryKey: ['components'],
    queryFn: async () => {
      const res = await api.get('/components');
      return res.data.data;
    }
  });

  const createMutation = useMutation({
    mutationFn: async (data: any) => {
      const res = await api.post(`/adaptations?agreementId=${agreementId}`, data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adaptations', agreementId] });
      handleCloseDialog();
    }
  });

  const handleOpenDialog = () => {
    setFormData({
      componentId: '',
      adaptationType: 'MODIFICATION',
      originalPlan: '',
      adaptedPlan: '',
      reason: ''
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setFormData({
      componentId: '',
      adaptationType: 'MODIFICATION',
      originalPlan: '',
      adaptedPlan: '',
      reason: ''
    });
  };

  const handleSubmit = () => {
    createMutation.mutate(formData);
  };

  const getAdaptationTypeColor = (type: string) => {
    switch (type) {
      case 'ADDITION':
        return 'success';
      case 'REMOVAL':
        return 'error';
      case 'MODIFICATION':
        return 'warning';
      default:
        return 'default';
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <Typography>Loading adaptations...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6">Component Adaptations</Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<Add />}
          onClick={handleOpenDialog}
        >
          Add
        </Button>
      </Box>

      {/* Adaptations */}
      {adaptations && adaptations.length > 0 ? (
        <Box display="flex" flexDirection="column" gap={2}>
          {adaptations.map((adaptation: any) => (
            <Card key={adaptation.id} variant="outlined">
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
                  <Box>
                    <Box display="flex" gap={1} alignItems="center" mb={1}>
                      <Chip
                        label={adaptation.adaptationType}
                        color={getAdaptationTypeColor(adaptation.adaptationType)}
                        size="small"
                      />
                      <Typography variant="subtitle2">
                        {adaptation.component.category}
                      </Typography>
                    </Box>
                    <Typography variant="body1" fontWeight="bold">
                      {adaptation.component.name}
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(adaptation.adaptedAt).toLocaleDateString()}
                  </Typography>
                </Box>
                <Box mb={2}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Adapted Plan:</strong> {adaptation.adaptedPlan}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Reason:</strong> {adaptation.reason}
                  </Typography>
                  {adaptation.originalPlan && (
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Original:</strong> {adaptation.originalPlan}
                    </Typography>
                  )}
                  {adaptation.outcome && (
                    <Typography variant="body2" color="success.main" gutterBottom>
                      <strong>Outcome:</strong> {adaptation.outcome}
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      ) : (
        <Alert severity="info">
          No adaptations recorded yet. Adaptations document how the plan was customized.
        </Alert>
      )}

      {/* Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>Document Adaptation</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            <FormControl fullWidth required>
              <InputLabel>Component</InputLabel>
              <Select
                value={formData.componentId}
                onChange={(e) => setFormData({ ...formData, componentId: e.target.value })}
                label="Component"
              >
                {components?.map((comp: any) => (
                  <MenuItem key={comp.id} value={comp.id}>
                    {comp.name} ({comp.category})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth required>
              <InputLabel>Type</InputLabel>
              <Select
                value={formData.adaptationType}
                onChange={(e) => setFormData({ ...formData, adaptationType: e.target.value })}
                label="Type"
              >
                <MenuItem value="ADDITION">Addition</MenuItem>
                <MenuItem value="REMOVAL">Removal</MenuItem>
                <MenuItem value="MODIFICATION">Modification</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Adapted Plan"
              multiline
              rows={3}
              value={formData.adaptedPlan}
              onChange={(e) => setFormData({ ...formData, adaptedPlan: e.target.value })}
              required
              helperText="Describe what was actually implemented"
            />

            <TextField
              fullWidth
              label="Original Plan (Optional)"
              multiline
              rows={2}
              value={formData.originalPlan}
              onChange={(e) => setFormData({ ...formData, originalPlan: e.target.value })}
              helperText="What was planned originally"
            />

            <TextField
              fullWidth
              label="Reason for Adaptation"
              multiline
              rows={2}
              value={formData.reason}
              onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
              required
              helperText="Why was this change made?"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!formData.componentId || !formData.adaptedPlan || !formData.reason || createMutation.isPending}
          >
            Record Adaptation
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdaptationTracker;

